import React from "react";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import CapabilityGrid from "./landing/platform/CapabilityGrid";
import DesignSteps from "./landing/platform/DesignSteps";
import JourneyStrip from "./landing/platform/JourneyStrip";
import MatchCriteria from "./landing/platform/MatchCriteria";
import OperatingModels from "./landing/platform/OperatingModels";
import PlatformHero from "./landing/platform/PlatformHero";
import {
  MediaCard,
  PlatformSection,
  PlatformShowcase,
  ShowcaseHeader,
} from "./landing/platform/PlatformShowcase";
import { SHOTS } from "./landing/platform/shots";

type Props = {
  pricingUrl: string;
};

export default function PlatformPage({ pricingUrl }: Props) {
  return (
    <LeadFormProvider>
      <PlatformHero />
      <JourneyStrip />
      <CapabilityGrid />

      <PlatformSection id="design" tone="tint">
        <PlatformShowcase
          headingId="design-heading"
          label="01 / Design the journey"
          heading="Start with structure, not a blank calendar."
          body="Configure the cohort around your organisation's goals, then give each mentee a practical journey connected to what they want to develop."
          bullets={[
            "Participant, role and cohort setup",
            "Programme goals, agendas and access",
            "SMART goals and session-by-session topics",
            "Action items, success measures and progress tracking",
          ]}
          shot={SHOTS.learningJourney}
          mediaSide="right"
          tone="tint"
        />
        <DesignSteps />
      </PlatformSection>

      <PlatformSection id="match">
        <PlatformShowcase
          headingId="match-heading"
          label="02 / Match with context"
          heading="Recommend mentors around the goal, not just a job title."
          body="We line up the mentee's development priorities with mentor experience. Your team matches faster."
          shot={SHOTS.mentorMatching}
          mediaSide="left"
        >
          <MatchCriteria />
        </PlatformShowcase>

        {/* A full-page portrait capture, so the panel crops it and the lightbox holds the rest. */}
        <div className="tw-mt-16 tw-rounded-[32px] tw-border tw-border-[#e2ecf4] tw-bg-[#fbfdfe] tw-p-12 sm:tw-mt-12 sm:tw-rounded-[24px] sm:tw-p-6">
          <PlatformShowcase
            label="Mentee view"
            heading="Show the match inside the participant's journey."
            body="Mentees can see the recommended mentor alongside the goal, session plan and reason for the match. The next step feels connected, not arbitrary."
            shot={SHOTS.learningJourneyMatching}
            mediaSide="right"
            mediaAspect={4 / 3}
            variant="panel"
          />
        </div>
      </PlatformSection>

      <PlatformSection id="engage" tone="dark">
        <ShowcaseHeader
          id="engage"
          label="03 / Run and engage"
          heading="Keep the programme moving after the launch."
          body="Let participants book and complete sessions while administrators see where engagement is healthy, slowing down or needs intervention."
          tone="dark"
        />

        <div className="tw-mt-12 tw-grid tw-grid-cols-2 tw-gap-6 sm:tw-grid-cols-1 sm:tw-gap-10">
          <MediaCard
            shot={SHOTS.sessionManagement}
            label="Session management"
            heading="Bookings, reminders and attendance in one workflow."
            body="Coordinate calendars, record completed sessions and see the status of every mentoring relationship."
          />
          <MediaCard
            shot={SHOTS.engagementTracking}
            label="Engagement visibility"
            heading="See who is active and who needs follow-up."
            body="Track participation, mentor and coach activity, booking status, completion and drop-off before problems become end-of-programme surprises."
          />
        </div>
      </PlatformSection>

      <PlatformSection id="progress" tone="tint">
        <PlatformShowcase
          headingId="progress-heading"
          label="04 / Turn sessions into progress"
          heading="Give every conversation a useful next step."
          body="After a session, participants can receive AI-generated summaries that organise what was discussed into practical outputs for the next stage of the journey."
          bullets={[
            "Key takeaways and discussion themes",
            "Agreed action items and follow-up",
            "Suggested next steps and resources",
            "Inputs that can support the participant's growth report",
          ]}
          shot={SHOTS.aiSummary}
          mediaSide="right"
          tone="tint"
        />
      </PlatformSection>

      <PlatformSection id="measure">
        <PlatformShowcase
          headingId="measure-heading"
          label="05 / Measure what matters"
          heading="Move from activity counts to a clearer programme story."
          body="Give HR, L&D and programme sponsors a shared view of how the cohort is performing, and the evidence needed to improve the next one."
          bullets={[
            "Completed sessions and engagement rates",
            "Feedback and mentor or coach ratings",
            "Goal progress and participation trends",
            "Exportable reports for internal stakeholders",
          ]}
          shot={SHOTS.reports}
          mediaSide="left"
        />
      </PlatformSection>

      <OperatingModels pricingUrl={pricingUrl} />
    </LeadFormProvider>
  );
}
